import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { env } from '../config';

export interface SeoProps {
  /** Page title WITHOUT the site suffix (added here). */
  title: string;
  description?: string;
  /** Route path for the canonical/og:url, e.g. '/about'. */
  path: string;
  /** Share image: site-relative path or absolute URL. */
  image?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  /** Use `title` verbatim (no " | Site" suffix) — the home page. */
  standalone?: boolean;
}

const absolute = (pathOrUrl: string) => new URL(pathOrUrl, env.VITE_SITE_URL).toString();

/**
 * index.html carries crawler-only defaults (`data-seo-static`, see
 * shared/seo/og.ts). They stay in place through loading states and are removed
 * the first time a route renders its own tags — otherwise every route would
 * carry two canonicals / descriptions.
 */
let staticTagsRemoved = false;
function useRetireStaticTags() {
  useEffect(() => {
    if (staticTagsRemoved) return;
    staticTagsRemoved = true;
    document.querySelectorAll('[data-seo-static]').forEach((el) => el.remove());
  }, []);
}

/**
 * Per-route head tags (title, description, canonical, Open Graph, Twitter).
 * Crawlers that do not run JS (Facebook/LinkedIn) read the static defaults in
 * index.html instead, which the og edge function rewrites for /stories/*.
 */
export function Seo({ title, description = env.VITE_SITE_DESCRIPTION, path, image = env.VITE_DEFAULT_OG_IMAGE, type = 'website', noindex, standalone }: SeoProps) {
  useRetireStaticTags();
  const siteName = env.VITE_SITE_NAME;
  const fullTitle = standalone ? title : `${title} | ${siteName}`;
  const url = absolute(path);
  const img = absolute(image);
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:type" content={type} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {noindex && <meta name="robots" content="noindex" />}
    </Helmet>
  );
}
